import { getCacheByType } from './raster';

export function serializeClip(clip) {
  // Enhanced error tolerance: handle null, undefined, and invalid clip objects
  if (!clip || typeof clip !== 'object') return null;

  // Safely extract values with fallbacks
  const x = typeof clip.x === 'number' && !isNaN(clip.x) ? clip.x : 0;
  const y = typeof clip.y === 'number' && !isNaN(clip.y) ? clip.y : 0;
  const w = typeof clip.w === 'number' && !isNaN(clip.w) ? clip.w : 0;
  const h = typeof clip.h === 'number' && !isNaN(clip.h) ? clip.h : 0;

  // Check if clip is empty (all values are 0)
  if (x === 0 && y === 0 && w === 0 && h === 0) {
    return null;
  }

  try {
    // Convert clip object to a compact string representation
    // Format: "x,y,w,h" where each value is rounded to 6 decimal places for precision
    return [
      Math.round(x * 1000000) / 1000000,
      Math.round(y * 1000000) / 1000000,
      Math.round(w * 1000000) / 1000000,
      Math.round(h * 1000000) / 1000000,
    ].join(',');
  } catch (error) {
    console.warn('Failed to serialize clip object:', clip, error);
    return null;
  }
}

export function deserializeClip(clipString) {
  // Enhanced error tolerance: handle various input types
  if (!clipString) return null;

  // Handle case where clipString is already an object
  if (typeof clipString === 'object') {
    return serializeClip(clipString)
      ? {
          x: typeof clipString.x === 'number' && !isNaN(clipString.x) ? clipString.x : 0,
          y: typeof clipString.y === 'number' && !isNaN(clipString.y) ? clipString.y : 0,
          w: typeof clipString.w === 'number' && !isNaN(clipString.w) ? clipString.w : 0,
          h: typeof clipString.h === 'number' && !isNaN(clipString.h) ? clipString.h : 0,
        }
      : null;
  }

  if (typeof clipString !== 'string') {
    console.warn('Invalid clip string type:', typeof clipString, clipString);
    return null;
  }

  try {
    // Trim whitespace and handle empty strings
    const trimmedString = clipString.trim();
    if (!trimmedString) return null;

    const parts = trimmedString.split(',');
    if (parts.length !== 4) {
      console.warn('Invalid clip string format, expected 4 parts:', clipString);
      return null;
    }

    const values = parts.map((part) => {
      const trimmedPart = part.trim();
      const num = parseFloat(trimmedPart);

      // Enhanced validation: check for valid numbers
      if (isNaN(num) || !isFinite(num)) {
        console.warn('Invalid clip value:', trimmedPart);
        return 0;
      }

      return num;
    });

    // Validate that all values are reasonable (not extremely large or negative where inappropriate)
    const [x, y, w, h] = values;

    return {
      x: x,
      y: y,
      w: w,
      h: h,
    };
  } catch (error) {
    console.warn('Failed to deserialize clip string:', clipString, error);
    return null;
  }
}

// Static empty clip value to avoid object recreation
const EMPTY_CLIP = { x: 0, y: 0, w: 0, h: 0 };

export function getNodeClip(node) {
  if (!node.clip) {
    return null;
  }

  // If clip is a string, deserialize it
  if (typeof node.clip === 'string') {
    const deserializedClip = deserializeClip(node.clip);
    return deserializedClip || EMPTY_CLIP;
  }

  // If clip is already an object, return it directly
  if (typeof node.clip === 'object') {
    return {
      x: node.clip.x || 0,
      y: node.clip.y || 0,
      w: node.clip.w || 0,
      h: node.clip.h || 0,
    };
  }

  // Fallback for any other type
  return null;
}

export function getClipImage(renderContext, node, img) {
  const _img = img.img;

  const clip = getNodeClip(node);

  // Return original image if no clipping is needed
  if (!clip || (clip.x === 0 && clip.y === 0 && clip.w === 0 && clip.h === 0)) {
    return {
      width: _img.width,
      height: _img.height,
      img: _img,
    };
  }

  // Cache management
  const caches = getCacheByType(renderContext, 'imageClip');
  const cacheItemKey = node.id;
  const cacheKey = `${node.src}:${clip.x}:${clip.y}:${clip.w}:${clip.h}`;
  const existingCache = caches.get(cacheItemKey);

  // Return cached result if valid
  if (existingCache && existingCache.key === cacheKey) {
    return existingCache;
  }

  // Clean up old cache
  if (existingCache?.dispose) {
    existingCache.dispose();
  }

  // Use original image dimensions to maintain high resolution
  const { width: imgWidth, height: imgHeight } = _img;

  // Calculate clipped dimensions based on original image size (keep original behavior)
  const clippedW = imgWidth * (1 + clip.w);
  const clippedH = imgHeight * (1 + clip.h);
  const clippedX = imgWidth * clip.x;
  const clippedY = imgHeight * clip.y;

  // Scale down to maximum canvas dimension
  // https://jhildenbiddle.github.io/canvas-size/#/?id=test-results
  const MAX_DIMENSION = 16384;
  const scale = Math.min(
    clippedW > MAX_DIMENSION ? MAX_DIMENSION / clippedW : 1,
    clippedH > MAX_DIMENSION ? MAX_DIMENSION / clippedH : 1,
  );

  // Create optimized canvas
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = Math.round(clippedW * scale);
  canvas.height = Math.round(clippedH * scale);

  // Enable image smoothing for better quality
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  context.scale(scale, scale);
  // Draw clipped image (keep original clipping behavior)
  context.drawImage(_img, -clippedX, -clippedY, imgWidth, imgHeight);

  // Create cache object with proper disposal
  const cache = {
    key: cacheKey,
    img: canvas,
    width: canvas.width,
    height: canvas.height,
    dispose() {
      // Properly dispose of canvas resources
      canvas.width = 0;
      canvas.height = 0;
      // Remove canvas from DOM if attached
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    },
  };

  // Store in cache
  caches.set(cacheItemKey, cache);

  return cache;
}
